import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ButtonBase } from '@bb-ui/react-library/dist/components/ButtonBase';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Duplicate } from '@bb-ui/icons/dist/small/Duplicate';
import { Check } from '@bb-ui/icons/dist/small/Check';
import { CopyOutcome, useCopyToClipboard } from './useCopyToClipboard';
import { useStyles } from './CopyButton.styles';

export interface CopyButtonProps {
    /** Text put on the clipboard. */
    text: string;
    /** Names the value, e.g. "Copy username". Visible text, or the accessible name when iconOnly. */
    label: string;
    /** For dense layouts only; the label then shows as a tooltip. */
    iconOnly?: boolean;
    successMessage?: string;
    /** Should tell the user how to copy by hand. */
    failureMessage?: string;
    /** Id of the element showing what is copied, when the label alone is generic. */
    describedBy?: string;
    testId?: string;
    className?: string;
    statusClassName?: string;
}

/**
 * The one copy-to-clipboard control. A successful copy turns the icon into a
 * check; a failure shows in a visible status line telling how to copy by hand.
 * The status line is rendered before any copy, so screen readers announce every
 * attempt. See README.md next to this file.
 */
export const CopyButton: React.FunctionComponent<CopyButtonProps> = ({
    text,
    label,
    iconOnly = false,
    successMessage,
    failureMessage,
    describedBy,
    testId,
    className,
    statusClassName,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { copy, outcome } = useCopyToClipboard(text);

    const messages: Record<CopyOutcome, string> = {
        copied: successMessage ?? t('general.copySuccess'),
        failed: failureMessage ?? t('general.copyFailure'),
    };
    const icon =
        outcome === 'copied' ? (
            <Check role="presentation" className={classes.copiedIcon} />
        ) : (
            <Duplicate role="presentation" />
        );

    const button = iconOnly ? (
        // isCustomTitle keeps the tooltip out of the accessibility tree, so
        // the name is not read twice.
        <Tooltip title={label} isCustomTitle>
            <IconButton
                className={classes.iconButton}
                onClick={copy}
                aria-label={label}
                aria-describedby={describedBy}
                data-testid={testId}
            >
                {icon}
            </IconButton>
        </Tooltip>
    ) : (
        <ButtonBase
            className={classes.textButton}
            onClick={copy}
            aria-describedby={describedBy}
            data-testid={testId}
        >
            <Typography
                variant="subtitle2"
                component="span"
                className={classes.buttonText}
            >
                {label}
            </Typography>
            {icon}
        </ButtonBase>
    );

    return (
        <div className={className}>
            {button}
            <Typography
                component="div"
                role="status"
                aria-live="polite"
                className={classnames(classes.status, statusClassName)}
                data-testid={testId && `${testId}-status`}
            >
                {outcome && (
                    <span
                        className={classnames({
                            // The check icon shows success; this text is for screen readers.
                            [classes.screenReaderOnly]: outcome === 'copied',
                        })}
                    >
                        {messages[outcome]}
                    </span>
                )}
            </Typography>
        </div>
    );
};
